import { channelName, postTime, ReachPost, shortDate, time, toDate } from './reach';
import { POST_STATUS } from '../statusWords';

/** Social › Posts' words (ReachPosts.tsx, backlog 4e7c78c8), kept apart to test. */

export type ListedPost = ReachPost & {
  /** automatic | campaign | you | agent */
  listSource?: string | null;
  campaignName?: string | null;
  /** The article or page the link opens, by name. */
  linkTitle?: string | null;
  channelHandle?: string | null;
};

/** The status control, in the designer's order; 0-count statuses are hidden. */
export const STATUSES: Array<{ id: string; label: string }> = [
  { id: 'waiting', label: POST_STATUS.waiting },
  { id: 'scheduled', label: POST_STATUS.scheduled },
  { id: 'posted', label: POST_STATUS.posted },
  { id: 'missed', label: POST_STATUS.missed },
  { id: 'draft', label: 'Drafts' },
  { id: 'skipped', label: POST_STATUS.skipped },
  { id: 'deleted', label: POST_STATUS.deleted },
];

/** Each source's words, the same in the Source column and the Source select. */
const SOURCE_LABEL: Record<string, string> = {
  automatic: 'Automatic',
  you: 'Written by you',
  agent: 'Written by your agent',
};

/** The Source column: where the post came from. */
export function sourceWords(p: ListedPost): string {
  if (p.listSource === 'campaign') return p.campaignName || 'A campaign';
  return SOURCE_LABEL[p.listSource || ''] || SOURCE_LABEL.agent;
}

/**
 * The Source select after "All sources": Automatic, each campaign by name,
 * Written by you, Written by your agent — only those with posts under the
 * other filters (counts.source), and always the one picked. With no counts
 * (an older server) every source is offered.
 */
export function sourceOptions(
  counts: Record<string, number> | undefined,
  campaigns: Array<{ id: string; name: string }>,
  picked: string
): Array<{ value: string; label: string }> {
  const all = [
    { value: 'automatic', label: SOURCE_LABEL.automatic },
    ...campaigns.map((c) => ({ value: c.id, label: c.name })),
    { value: 'you', label: SOURCE_LABEL.you },
    { value: 'agent', label: SOURCE_LABEL.agent },
  ];
  if (!counts) return all;
  return all.filter((o) => counts[o.value] || o.value === picked);
}

export interface PostFilters {
  channel: string | null;
  status: string | null;
  source: string;
  q: string;
  sort: 'newest' | 'oldest';
}

/** The filters kept in the address (designer spec: ch, st, src, q, sort). */
export function readPostFilters(params: URLSearchParams): PostFilters {
  const st = params.get('st');
  return {
    channel: params.get('ch') || null,
    status: st && STATUSES.some((s) => s.id === st) ? st : null,
    source: params.get('src') || '',
    q: params.get('q') || '',
    sort: params.get('sort') === 'oldest' ? 'oldest' : 'newest',
  };
}

/** The address's params for these filters, keeping any others it has; defaults are left out. */
export function writePostFilters(params: URLSearchParams, f: PostFilters): URLSearchParams {
  const next = new URLSearchParams(params);
  const set = (key: string, value: string | null) => {
    if (value) next.set(key, value);
    else next.delete(key);
  };
  set('ch', f.channel);
  set('st', f.status);
  set('src', f.source);
  set('q', f.q.trim());
  set('sort', f.sort === 'oldest' ? 'oldest' : null);
  return next;
}

/** The Post column's second line: "→ Fall price guide", the address's host, or "No link". */
export function linkWords(p: ListedPost): string {
  if (p.linkTitle) return `→ ${p.linkTitle}`;
  if (!p.postUrl) return 'No link';
  try {
    return `→ ${new URL(p.postUrl).host.replace(/^www\./, '')}`;
  } catch {
    return `→ ${p.postUrl}`;
  }
}

/** The When column: "Tue, Oct 28" over "9:30 AM"; a draft says when it was saved. */
export function whenWords(p: ListedPost, now = new Date()): { day: string; at: string } {
  // A deleted post goes by when it was deleted: its own time may be gone.
  const t = p.deletionStatus ? toDate(p.updatedAt) : postTime(p);
  if (!t) {
    const saved = toDate(p.updatedAt);
    return { day: 'No date yet', at: saved ? `Saved ${shortDate(saved, now)}` : '' };
  }
  return {
    day: t.toLocaleDateString([], {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: t.getFullYear() === now.getFullYear() ? undefined : 'numeric',
    }),
    at: time(t),
  };
}

/** "{n} posts" when every match is on screen, else "Showing n of N posts". */
export function footerWords(shown: number, total: number): string {
  const posts = (n: number) => `${n.toLocaleString()} ${n === 1 ? 'post' : 'posts'}`;
  return shown >= total ? posts(total) : `Showing ${shown.toLocaleString()} of ${posts(total)}`;
}

/**
 * The line under a row's status (Social Posts dev spec, round 31; QA
 * 8e71b8c7): why a post didn't post, or that a coming one won't because its
 * channel is disconnected. `active` is the channel's connection (undefined:
 * not known, said nothing about). A post with its own reason says that, not the
 * time passing (backlog 052cbd08); the reason is the server's plain words.
 */
export function statusReason(p: ListedPost, active: boolean | undefined): string | null {
  if (p.deletionStatus) return null;
  const net = channelName(p.platform);
  if (p.reachStatus === 'missed') {
    if (active === false) return `Time passed while ${net} was disconnected`;
    if (!p.failure || /before its time passed/i.test(p.failure)) {
      return 'Time passed without posting';
    }
    return p.failure.charAt(0).toUpperCase() + p.failure.slice(1);
  }
  if (active === false && ['scheduled', 'publishing', 'waiting'].includes(p.reachStatus)) {
    return `Won’t post: ${net} is disconnected`;
  }
  return null;
}

/**
 * The chips after the connected ones: each disconnected channel that has posts
 * under the other filters, so the chips add up to All channels.
 */
export function disconnectedChannels<T extends { id: string; isActive: boolean }>(
  channels: T[],
  counts: Record<string, number>
): T[] {
  return channels.filter((c) => !c.isActive && counts[c.id] > 0);
}
